// components/ArticlePreview.tsx
"use client";
import React, { useEffect, useState } from "react";
import Image from "next/image";

import Link from "next/link";
import CustomDateComponent from "@/components/ui/CustomDateComponent";
import { ArticleData } from "@/types/article.types";
import { useAuth } from "@/context/AuthContext";
import { useRouter } from "next/navigation";
import { articleService } from "@/services/articleService";
import { BookmarkIcon } from "lucide-react";
import { SessionUser } from "@/types/common";
// import { saveCurrentUrl } from "@/utils/utils";
import ReactPlayer from "react-player";
import { isSameDate } from "@/services/commonFunction";
import { useSnackbar } from "@/context/SnackbarContext";
import { matchSiteConfig } from "@/utils/getApiBaseUrl";
import GumletImage from "@/utils/GumletSrcSet";
import ViewCount from "@/app/[post_type]/[category]/[slug]/ViewCount";

interface ArticlePreviewProps {
  data: ArticleData;
  host?: string;
  slug?: string;
}

const ArticlePreview: React.FC<ArticlePreviewProps> = ({ data, host, slug }) => {
  const router = useRouter();
  const { sessionUser } = useAuth();
  const { showSnackbar } = useSnackbar();
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [domainConfig, setDomainConfig] = useState({
    whatsappUrl: "https://www.whatsapp.com/channel/0029VadjVKf0AgWIpYe2d81k",
    googleNewsUrl: "https://bit.ly/3CPWqBG",
    siteType: "UNKNOWN",
    // apiUrl: "",
  });

  useEffect(() => {
    // Get domain configuration on component mount
    const host = window.location.host;
    const { siteType } = matchSiteConfig(host);
    
    // Set WhatsApp and Google News URLs based on site type
    let whatsappUrl = "https://whatsapp.com/channel/0029VadjVKf0AgWIpYe2d81k";
    let googleNewsUrl = "https://bit.ly/3CPWqBG";
    
    switch (siteType) {
      case "IN":
        whatsappUrl = "https://whatsapp.com/channel/0029VadjVKf0AgWIpYe2d81k";
        googleNewsUrl = "https://bit.ly/3CPWqBG";
        break;
      case "GLOBAL":
        whatsappUrl = "https://whatsapp.com/channel/0029VaZHMv7L7UVZsBEtjM1C";
        googleNewsUrl = "https://news.google.com/publications/CAAqNQgKIi9DQklTSFFnTWFoa0tGM0JsYjNCc1pXMWhkSFJsY25ObmJHOWlZV3d1WTI5dEtBQVAB?ceid=IN:en&oc=3 ";
        break;
      default:
        whatsappUrl = "https://whatsapp.com/channel/0029VadjVKf0AgWIpYe2d81k";
        googleNewsUrl = "https://bit.ly/3CPWqBG";
        break;
    }
    
    setDomainConfig({
      whatsappUrl,
      googleNewsUrl,
      siteType,
      // apiUrl,
    });
    
    // console.log(`Site Type: ${siteType}`);
  }, []);

  const handleShare = (platform: string) => {
    const url = data?.fullpath
      ? `${process.env.NEXT_PUBLIC_SITE_URL}${data.fullpath}`
      : window.location.href;
    const title = data?.title || "";

    let shareUrl = "";
    switch (platform) {
      case "facebook":
        const cleanUrl = url.split("?")[0];
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
          cleanUrl
        )}`;
        break;
      case "twitter":
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(
          url
        )}&text=${encodeURIComponent(title)}`;
        break;
      case "linkedin":
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
          url
        )}`;
        break;
      case "whatsapp":
        shareUrl = `https://wa.me/?text=${encodeURIComponent(
          `${title} ${url}`
        )}`;
        break;
    }

    if (shareUrl) {
      // window.open(shareUrl, "_blank", "width=600,height=400");
      window.open(shareUrl, "_blank");
    }
  };

  const handleBookmark = async () => {
    const article_id = Number(data?.post_id) || 0;
    if (
      !sessionUser?.subscriber_id ||
      !sessionUser?.user_email ||
      article_id <= 0
    ) {
      if (article_id <= 0) {
        return;
      }
      // Store the current page URL before redirecting to signup
      // saveCurrentUrl();
      router.push("/auth/login");
      return;
    }
    if (
      sessionUser?.subscriber_id &&
      sessionUser?.user_email &&
      article_id > 0
    ) {
      try {
        const token =
          sessionUser?.token || localStorage.getItem("peoples_token") || "";
        await articleService.articleBookmark(article_id, token);
        getBookmarkArticleList(Number(data?.post_id) || 0, sessionUser);
        showSnackbar({
          message: "Article bookmarked successfully",
          type: "success",
        });
      } catch (error) {
        console.error("articleBookmark error:", error);
        showSnackbar({
          message: "Bookmark failed",
          type: "error",
        });
      }
    }

    // if (navigator.share) {
    //   navigator
    //     .share({
    //       title: data?.title,
    //       text: data?.description,
    //       url: window.location.href,
    //     })
    //     .catch(console.error);
    // } else {
    //   navigator.clipboard.writeText(window.location.href);
    //   console.log("Link copied to clipboard!");
    // }
  };

  useEffect(() => {
    if (data?.post_id && sessionUser?.subscriber_id) {
      getBookmarkArticleList(Number(data?.post_id) || 0, sessionUser);
    }
  }, [data, sessionUser]);

  const getBookmarkArticleList = async (
    postId: number,
    sessionUser: SessionUser
  ) => {
    try {
      const token =
        sessionUser?.token || localStorage.getItem("peoples_token") || "";
      const response = await articleService.getBookmarkArticle({
        postId,
        token,
      });
      console.log("getBookmarkArticleList response", response);
      setIsBookmarked(response?.data?.asm_is_saved || false);
    } catch (error) {
      console.error("articleBookmark error:", error);
    }
  };

  const socialLink = data?.authors?.[0]?.social_link || "";
  const validLink = socialLink?.startsWith("http")
    ? socialLink
    : `https://${socialLink}`;

  return (
    <div className="mx-auto font-sans">
      <div className="w-full flex-col gap-1">
        <h3 className="text-gray-600 text-[12px] font-medium capitalize">
          <Link
            href={
              data?.category?.[0]?.parent
                ? `/category/${data?.category?.[0]?.parent?.slug}/${data?.category?.[0]?.slug}`
                : `/category/${data?.category?.[0]?.slug}`
            }
            prefetch={false}
          >
            {data?.category?.[0]?.name || ""}
          </Link>
        </h3>
        <div className="flex items-center justify-between lg:gap-8 gap-2">
          <h1
            itemProp="headline"
            className="text-[32px]  lg:text-4xl font-medium text-gray-900 mb-8"
          >
            {data?.title || ""}
          </h1>
          {data?.brand && Object.keys(data.brand).length > 0 && (
            <div className="h-full w-28 aspect-square relative border border-inactiveGray rounded-lg  p-2 overflow-hidden flex-shrink-0">
              <GumletImage
                imageUrl={data.brand?.image}
                fallbackSrc="/assets/images/dummy.jpg"
                alt="Brand logo"
                fill
                className="object-cover"
              />
            </div>
          )}
        </div>
      </div>
      {data?.meta_data?.modified_time && (
        <meta
          itemProp="datePublished"
          content={data?.meta_data?.published_time || ""}
        ></meta>
      )}
      {data?.meta_data?.modified_time && (
        <meta
          itemProp="dateModified"
          content={data?.meta_data?.modified_time || ""}
        ></meta>
      )}

      {/* Author info and metadata */}
      {(data?.authors ?? []).length > 0 && (
        <div
          itemProp="author"
          className="flex flex-col md:flex-row md:items-center items-start md:gap-0 gap-6 justify-between mb-8"
        >
          <div className="flex items-center space-x-3">
            <div className="relative w-16 h-16 rounded-full overflow-hidden">
              <Link href={`/author/${data?.authors?.[0]?.url}`} prefetch={false}>
                <GumletImage
                  imageUrl={
                    data?.authors?.[0]?.profile_image}
                  fallbackSrc="/assets/profile.svg"
                  alt={data?.authors?.[0]?.full_name || ""}
                  fill
                  itemProp="image"
                  className="object-contain"
                />
              </Link>
            </div>
            <div className="flex flex-col gap-1">
              <div className="flex items-center space-x-2">
                <Link
                  href={`/author/${data?.authors?.[0]?.url}`}
                  itemProp="url"
                  className="font-normal text-base capitalize"
                  prefetch={false}
                >
                  <span className="whitespace-nowrap" itemProp="name">
                    {data?.authors?.[0]?.full_name
                      ? data?.authors?.[0]?.full_name
                      : `${data?.authors?.[0]?.first_name || ""} ${
                          data?.authors?.[0]?.last_name || ""
                        }`}
                  </span>
                </Link>
                <span className="text-gray-400 md:flex hidden">•</span>
                <Link
                  href={domainConfig.whatsappUrl}
                  target="_blank"
                  className="text-gray-400 rounded-md hover:text-gray-500 items-center h-7 md:flex hidden"
                  prefetch={false}
                >
                  <Image
                    src="/assets/Join-us.svg"
                    alt="Join Us"
                    width={100}
                    height={28}
                    className="h-full w-full object-contain"
                  />
                  {/* <Image src="https://res.cloudinary.com/people-matters/image/upload/v1720182082/whatsapp_hkuoys.svg" alt="Whatsapp" width={100} height={28} /> */}
                </Link>
                <Link
                  href={domainConfig.googleNewsUrl}
                  target="_blank"
                  className="text-gray-400 rounded-md hover:text-gray-500 items-center h-7 md:flex hidden"
                  prefetch={false}
                >
                  <Image
                    src="/assets/googlenews.svg"
                    alt="Google News"
                    width={100}
                    height={28}
                    className="h-full w-full object-contain"
                  />
                  {/* <Image src="https://res.cloudinary.com/people-matters/image/upload/v1720182113/googlenews_vzribs.svg" alt="Google News" width={100} height={28} /> */}
                </Link>
              </div>
              <div className="flex items-center text-gray-500 text-sm flex-wrap">
                {/* {JSON.stringify(data?.authors?.[0]?.social_link )} */}
                {data?.authors?.[0]?.social_link &&
                  data?.authors?.[0]?.social_link !== "null" &&
                  validLink && (
                    <>
                      <Link
                        href={validLink || ""}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="relative h-3 w-3 mb-1"
                        prefetch={false}
                      >
                        <Image src="/assets/linkedIn.svg" alt="LinkedIn" fill />
                      </Link>
                      <span className="mx-1">|</span>
                    </>
                  )}
                {data?.authors?.[0]?.email &&
                  data?.authors?.[0]?.social_link && (
                    <span className="mx-1">|</span>
                  )}

                {data?.read_duration && (
                  <>
                    <span className="text-xs">
                      {data?.read_duration} mins read
                    </span>
                    <span className="mx-1">|</span>
                  </>
                )}
                <CustomDateComponent
                  date={data?.meta_data?.published_time || ""}
                  format="dayFirst"
                  className="text-xs"
                />
                {!isSameDate(
                  data?.meta_data?.published_time,
                  data?.meta_data?.modified_time
                ) && (
                  <>
                    <span className="mx-2">|</span>
                    <div className="text-xs">
                      Updated on{" "}
                      <CustomDateComponent
                        date={data?.meta_data?.modified_time || ""}
                        format="dayFirst"
                        className="text-xs"
                      />
                    </div>
                  </>
                )}
                {slug && host && <ViewCount hidden slug={slug} host={host} />}
                {(data?.brand && Object.keys(data.brand).length > 0) ||
                  (data?.contentWall?.post_Wall?.code === "FORM" && (
                    <span className="mx-2">|</span>
                  ))}
                {(data?.brand && Object.keys(data.brand).length > 0) ||
                  (data?.contentWall?.post_Wall?.code === "FORM" && (
                    <p className="text-xs text-orange">BRAND INITIATIVE</p>
                  ))}
              </div>
            </div>
          </div>

          {/* Share buttons */}
          <div className="flex items-center justify-between w-full md:w-auto">
            <div className="flex items-center gap-6 social-icon-container">
              <button
                onClick={handleBookmark}
                // className="text-gray-400 hover:text-gray-600 transition-colors"
                className={`rounded flex items-center justify-center ${
                  isBookmarked
                    ? "bg-orange-100 text-orange"
                    : "bg-transparent text-gray-400 hover:text-gray-600"
                } transition-colors`}
                title="Share or Bookmark"
                style={{ minWidth: "20px", minHeight: "20px" }}
               aria-label="Bookmark">
                {/* <BookmarkIcon width={20} height={20} /> */}
                <BookmarkIcon
                  // color={isBookmarked ? "#f17c06" : "#000"}
                  // fill={isBookmarked ? "#f17c06" : "#fff"}
                  width={20}
                  height={20}
                />
                {/* <Image
                src="/assets/bookmarkGray.svg"
                alt="Share"
                width={16}
                height={16}
              /> */}
              </button>
              <button
                onClick={() => handleShare("facebook")}
                className="text-gray-400 hover:text-gray-600 transition-colors flex items-center justify-center"
                title="Share on Facebook"
                style={{ minWidth: "20px", minHeight: "20px" }}
               aria-label="Share">
                {/* <Image
                  src="/assets/facebook.svg"
                  alt="Share on Facebook"
                  width={9}
                  height={16}
                /> */}
                <svg
                  width="9"
                  height="16"
                  viewBox="0 0 9 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M5.56003 9.28489V15.8962H2.52304V9.28489H0V6.60416H2.52304V5.62882C2.52304 2.00779 4.03569 0.103836 7.23621 0.103836C8.21739 0.103836 8.46269 0.261526 9 0.390014V3.04154C8.39844 2.93641 8.22907 2.87801 7.60415 2.87801C6.86243 2.87801 6.46528 3.08826 6.10318 3.50293C5.74108 3.91759 5.56003 4.63596 5.56003 5.66386V6.61H9L8.07722 9.29073H5.56003V9.28489Z"
                    fill="currentColor"
                  />
                </svg>
              </button>

              <button
                onClick={() => handleShare("linkedin")}
                className="text-gray-400 hover:text-gray-600 transition-colors flex items-center justify-center"
                title="Share on LinkedIn"
                style={{ minWidth: "20px", minHeight: "20px" }}
               aria-label="Share">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ display: "block", maxWidth: "100%", height: "auto" }}
                >
                  <path
                    d="M3.61673 5.26138H0.273438V15.9229H3.61673V5.26138Z"
                    fill="currentColor"
                  />
                  <path
                    d="M12.6583 5.03028C12.5351 5.01487 12.4041 5.00717 12.2732 4.99947C10.4012 4.92243 9.34587 6.03173 8.9761 6.50934C8.87596 6.6403 8.82973 6.71733 8.82973 6.71733V5.2922H5.63281V15.9537H8.82973H8.9761C8.9761 14.8676 8.9761 13.7891 8.9761 12.7029C8.9761 12.1174 8.9761 11.532 8.9761 10.9465C8.9761 10.2224 8.92218 9.45205 9.28424 8.78955C9.59237 8.23491 10.147 7.95758 10.771 7.95758C12.6198 7.95758 12.6583 9.62923 12.6583 9.78329C12.6583 9.791 12.6583 9.7987 12.6583 9.7987V16H16.0016V9.04377C16.0016 6.66341 14.7922 5.26138 12.6583 5.03028Z"
                    fill="currentColor"
                  />
                  <path
                    d="M1.94126 3.88253C3.01339 3.88253 3.88253 3.01339 3.88253 1.94126C3.88253 0.869133 3.01339 0 1.94126 0C0.869129 0 0 0.869133 0 1.94126C0 3.01339 0.869129 3.88253 1.94126 3.88253Z"
                    fill="currentColor"
                  />
                </svg>
              </button>
              <button
                onClick={() => handleShare("twitter")}
                className="text-gray-400 hover:text-gray-600 transition-colors flex items-center justify-center"
                title="Share on Twitter"
                style={{ minWidth: "20px", minHeight: "20px" }}
               aria-label="Share">
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ display: "block", maxWidth: "100%", height: "auto" }}
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M5.67227 0.875H0L6.72535 9.71192L0.430223 17.125H3.33876L8.09997 11.5183L12.3277 17.0735H18L11.0793 7.97977L11.0915 7.99545L17.0504 0.978232H14.1418L9.71667 6.18932L5.67227 0.875ZM3.131 2.42263H4.89685L14.869 15.5258H13.1032L3.131 2.42263Z"
                    fill="currentColor"
                  />
                </svg>
              </button>
            </div>
            {/* Mobile-only links positioned to the right */}
            <div className="md:hidden flex items-center gap-2 ml-auto mobile-social-container">
              <Link
                href={domainConfig.whatsappUrl}
                target="_blank"
                className="text-gray-400 rounded-md hover:text-gray-500 flex items-center justify-center flex-shrink-0"
                style={{ minWidth: "28px", minHeight: "28px" }}
                prefetch={false}
              >
                <Image
                  src="/assets/logos/whatsapp.svg"
                  alt="Join Us"
                  width={28}
                  height={28}
                  className="block"
                  style={{
                    display: "block",
                    width: "28px",
                    height: "28px",
                    objectFit: "contain",
                  }}
                />
              </Link>
              <Link
                href={domainConfig.googleNewsUrl}
                target="_blank"
                className="text-gray-400 rounded-md hover:text-gray-500 flex items-center justify-center flex-shrink-0"
                style={{ minWidth: "28px", minHeight: "28px" }}
                prefetch={false}
              >
                <Image
                  src="/assets/logos/google-news.svg"
                  alt="Google News"
                  width={28}
                  height={28}
                  className="block"
                  style={{
                    display: "block",
                    width: "28px",
                    height: "28px",
                    objectFit: "contain",
                  }}
                />
              </Link>
            </div>
          </div>
        </div>
      )}

      {data?.post_type?.name === "VIDEO" ? (
        <div className="relative w-full mb-8 aspect-video">
          {data?.video_id ? (
            <ReactPlayer
              style={{ width: "100%", height: "100%", aspectRatio: "16/9" }}
              controls
              src={`https://www.youtube.com/embed/${data?.video_id}`}
              light={data?.hero_image}
              playing={true}
            />
          ) : data?.featuredVideo ? (
            <ReactPlayer
              style={{
                width: "100%",
                height: "auto",
                aspectRatio: "16/9",
                marginBottom: "16px",
              }}
              controls
              src={data?.featuredVideo}
              light={data?.hero_image}
              playing={true}
            />
          ) : data?.hero_image ? (
            <GumletImage
              imageUrl={data.hero_image}
              fallbackSrc="/assets/images/dummy.jpg"
              alt="Article cover image"
              itemProp={"image"}
              fill
              className="object-cover rounded-[4px]"
              fetchPriority="high"
            />
          ) : null}
        </div>
      ) : data?.hero_image ? (
        <div className="relative w-full mb-8 aspect-video">
          <GumletImage
            imageUrl={data.hero_image}
            fallbackSrc="/assets/images/dummy.jpg"
            alt="Article cover image"
            itemProp={"image"}
            fill
            className="object-cover rounded-[4px]"
            fetchPriority="high"
          />
        </div>
      ) : null}

      <div className="lg:mb-8">
        <p className="text-[#171717c1]">{data?.description || ""}</p>
      </div>
    </div>
  );
};

export default ArticlePreview;
